import { useCallback, useState } from 'react';
import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom';
import { api, signOut } from '../api';
import { useSession } from './session-context';
import { centralLoginUrl } from './surface-links';
import { runSignOut, useSigningOut } from './sign-out-progress';
import { useResource } from './use-resource';
import { ErrorState } from './state-view';
import { PlanProvider } from './plan-context';
import { Rail } from './rail';
import { TopBar } from './top-bar';
import { ContextBar } from './context-bar';
import { TabBar } from './tab-bar';
import { MoreSheet } from './more-sheet';
import { CommandPalette } from './command-palette';
import { HelpPanel, ContactSheet } from './help-sheets';
import type { MerchantBookingList } from '../mock/seeds/schema-types';
import './dash.css';
import './shell-chrome.css';

// The persistent merchant-dashboard shell (DC70; PRD v2.5 §7.3.3, DR-37, DR-45) —
// pure wiring of the CB-17 chrome files: the glass rail (≥ 900 px), the top bar, the
// context bar, the routed screen, the 4-slot mobile bar + More sheet (< 900 px), and
// the shell overlays (⌘K palette, Help panel, Contact sheet; the notification centre
// and user menu live in the top bar). It owns only the cross-chrome state: which
// overlay is open, the collapsed rail (a per-device convenience in localStorage), the
// pending-proofs badge count and sign-out.
const RAIL_KEY = 'bd.merchant.rail';

// Pages already converted to the v2 design lay their own cards straight on the
// canvas (no shared white pane). Each Phase 5 screen task adds its route here.
const BARE_PANE_ROUTES = ['/', '/bookings', '/services', '/business', '/offers', '/team', '/insights', '/plan', '/account'];

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(RAIL_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

type Overlay = null | 'search' | 'notif' | 'menu' | 'more' | 'help' | 'contact';

export function DashLayout() {
  const { user, activeBusiness, scope, loading, error, epoch, reload, isStaff } = useSession();
  const location = useLocation();
  const [overlay, setOverlay] = useState<Overlay>(null);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const signingOut = useSigningOut();

  // Bookings badge (FR-B11a): the Payment check queue in the dashboard's business scope
  // — the same read and count the Bookings screen's Payment check tab shows (API-95
  // `tab=payment_check`, `business_id=all` in the all-businesses scope), so the rail,
  // the mobile bar and the tab never disagree. Re-read on navigation so a just-verified
  // proof drops off. A team member cannot verify (merchant-only, API-16): no badge.
  const scopeId = scope === 'all' ? 'all' : activeBusiness?.id;
  const { data: pendingCount } = useResource<number>(
    async (signal) => {
      if (!scopeId || isStaff) return 0;
      const r = (await api.GET('/merchant/bookings', {
        params: { query: { business_id: scopeId, tab: 'payment_check', limit: 1 } },
        signal,
      })) as MerchantBookingList;
      return r.total_estimate ?? r.items.length;
    },
    [scopeId, epoch, location.pathname, isStaff],
  );
  const pending = pendingCount ?? 0;

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try {
        if (next) localStorage.setItem(RAIL_KEY, 'collapsed');
        else localStorage.removeItem(RAIL_KEY);
      } catch {
        /* storage blocked — the choice holds for this session */
      }
      return next;
    });
  };

  const openOnly = useCallback((o: Overlay) => setOverlay(o), []);
  const closeIf = useCallback((o: Overlay) => setOverlay((cur) => (cur === o ? null : cur)), []);
  const onSearch = useCallback(() => setOverlay('search'), []);

  // Leave for the central login deterministically — a soft reload can't sign you out
  // while a restored session (or the dev mock) still answers GET /me. runSignOut runs
  // it once, disables every Log out control and shows the "Logging out…" veil; it
  // leaves on a failed call too (the in-memory token is already dropped).
  const onLogout = () => {
    void runSignOut(signOut, () => {
      window.location.href = centralLoginUrl();
    });
  };

  return (
    <PlanProvider>
      <div className={`dash-shell${collapsed ? ' is-rail-collapsed' : ''}`}>
        <Rail
          pending={pending}
          isStaff={isStaff}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
          onHelp={() => openOnly('help')}
          onContact={() => openOnly('contact')}
          onLogout={onLogout}
          loggingOut={signingOut}
        />

        <main className="dash-main">
          <TopBar
            onSearch={onSearch}
            notifOpen={overlay === 'notif'}
            onNotifOpenChange={(o) => (o ? openOnly('notif') : closeIf('notif'))}
            menuOpen={overlay === 'menu'}
            onMenuOpenChange={(o) => (o ? openOnly('menu') : closeIf('menu'))}
            isStaff={isStaff}
            onHelp={() => openOnly('help')}
            onContact={() => openOnly('contact')}
            onLogout={onLogout}
            loggingOut={signingOut}
          />
          <ContextBar />
          <div className={BARE_PANE_ROUTES.includes(location.pathname) ? 'dash-pane dash-pane--bare' : 'dash-pane'}>
            {/* Screens mount once the session is known, so a team member's first render
                never fires an owner's reads (the AuthGate holds the app until then too). */}
            {error && !loading ? <ErrorState onRetry={reload} /> : user ? <Outlet /> : null}
          </div>
        </main>

        <TabBar pending={pending} isStaff={isStaff} moreOpen={overlay === 'more'} onMore={() => openOnly('more')} />
        <MoreSheet
          open={overlay === 'more'}
          onClose={() => closeIf('more')}
          isStaff={isStaff}
          pending={pending}
        />
        <CommandPalette open={overlay === 'search'} onClose={() => closeIf('search')} />
        <HelpPanel open={overlay === 'help'} onClose={() => closeIf('help')} />
        <ContactSheet open={overlay === 'contact'} onClose={() => closeIf('contact')} />

        <ScrollRestoration />
      </div>
    </PlanProvider>
  );
}
